import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Edit2,
  Loader2,
  Lock,
  Plus,
  Trash2,
  X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Biweekly Timesheet & Payroll Approval page.
 * URL: /timesheet
 *
 * Flow:
 *   1. Unlock with manager/master/HR PIN
 *   2. Pick date range (default = current biweekly period, Sun→Sat)
 *   3. Review per-employee: hours, OT, pay
 *   4. Edit individual punches inline
 *   5. Approve one-by-one or bulk-approve for payroll
 */
export default function TimesheetPage() {
  const navigate = useNavigate();
  const [locked, setLocked] = useState(true);
  const [pin, setPin] = useState("");
  const [actor, setActor] = useState(null);
  const [{ start, end }, setRange] = useState(() => defaultBiweekly());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const unlock = async () => {
    try {
      const res = await axios.post(`${API}/master/unlock`, { password: pin.trim() });
      const tier = res.data.tier;
      if (!["master", "manager", "hr"].includes(tier)) {
        toast.error("No access");
        return;
      }
      setActor({
        code: res.data.manager_code || pin.trim(),
        name: res.data.master_name,
        tier,
      });
      setLocked(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid PIN");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/timesheet`, { params: { start, end } });
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load timesheet");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!locked) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked, start, end]);

  const shift = (days) => {
    const s = addDays(start, days);
    const e = addDays(end, days);
    setRange({ start: s, end: e });
  };

  const approveOne = async (code) => {
    try {
      await axios.post(`${API}/timesheet/approve`, {
        employee_code: code, start_date: start, end_date: end, approver_code: actor.code,
      });
      toast.success("Approved");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    }
  };
  const unapproveOne = async (code) => {
    try {
      await axios.post(`${API}/timesheet/unapprove`, {
        employee_code: code, start_date: start, end_date: end, approver_code: actor.code,
      });
      toast.success("Unapproved");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unapprove failed");
    }
  };
  const approveAll = async () => {
    if (!window.confirm(`Approve every pending timesheet in ${start} → ${end}?`)) return;
    try {
      const res = await axios.post(`${API}/timesheet/approve-all`, {
        start_date: start, end_date: end, approver_code: actor.code,
      });
      toast.success(`Approved ${res.data.newly_approved} timesheets`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk approve failed");
    }
  };

  const rowsFiltered = useMemo(() => {
    if (!data) return [];
    return data.rows;
  }, [data]);

  if (locked) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <Card className="bg-[#1a1a1c] border-[#2a2a2c] p-8 w-full max-w-sm text-center">
          <div className="w-14 h-14 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-blue-300" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Timesheet</h1>
          <p className="text-sm text-gray-400 mb-6">Manager PIN to review + approve for payroll</p>
          <Input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && unlock()}
            placeholder="PIN"
            className="bg-[#252527] border-[#3a3a3c] text-white text-center text-xl tracking-widest h-12 mb-3"
            autoFocus
            data-testid="ts-pin"
          />
          <Button onClick={unlock} className="w-full bg-blue-500 hover:bg-blue-600 text-white h-11" data-testid="ts-unlock">
            Unlock
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0f10] text-white">
      {/* Top bar */}
      <div className="border-b border-[#2a2a2c] bg-[#1a1a1c] px-4 py-3 flex items-center justify-between gap-3">
        <Button variant="ghost" onClick={() => navigate("/paypunch")} className="text-gray-400 hover:text-white" data-testid="ts-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex-1 text-center">
          <div className="text-xs text-gray-500 uppercase tracking-widest">Biweekly Timesheet</div>
          <div className="text-base font-semibold">{fmtDate(start)} → {fmtDate(end)}</div>
        </div>
        <div className="text-right text-xs text-gray-400">
          <div>{actor.name}</div>
          <Badge className="bg-blue-500/20 text-blue-300 border-0 text-[9px] mt-0.5">{actor.tier.toUpperCase()}</Badge>
        </div>
      </div>

      {/* Range controls */}
      <div className="border-b border-[#2a2a2c] bg-[#141416] px-4 py-3 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="ghost" onClick={() => shift(-14)} className="text-gray-400 hover:text-white h-9" data-testid="ts-prev">
          <ChevronLeft className="w-4 h-4" /> Prev 2 weeks
        </Button>
        <Input type="date" value={start} onChange={(e) => setRange({ start: e.target.value, end })} className="bg-[#252527] border-[#3a3a3c] text-white w-40 h-9" data-testid="ts-start" />
        <span className="text-gray-500 text-sm">→</span>
        <Input type="date" value={end} onChange={(e) => setRange({ start, end: e.target.value })} className="bg-[#252527] border-[#3a3a3c] text-white w-40 h-9" data-testid="ts-end" />
        <Button size="sm" variant="ghost" onClick={() => shift(14)} className="text-gray-400 hover:text-white h-9" data-testid="ts-next">
          Next 2 weeks <ChevronRight className="w-4 h-4" />
        </Button>
        <div className="flex-1" />
        <div className="text-[10px] text-gray-500 uppercase tracking-widest px-2 py-1 bg-[#252527] rounded">
          Regular (W-2) only · Cash employees separate
        </div>
        {data && data.totals.pending_count > 0 && (
          <Button onClick={approveAll} className="bg-green-500 hover:bg-green-600 text-white h-9" data-testid="ts-approve-all">
            <Check className="w-4 h-4 mr-1" /> Approve all pending ({data.totals.pending_count})
          </Button>
        )}
      </div>

      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        {loading && !data && (
          <div className="text-center text-gray-500 py-10">
            <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> loading…
          </div>
        )}

        {/* Totals + trend */}
        {data && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
              <Stat label="Employees" value={data.employee_count} />
              <Stat label="Reg hours" value={fmt(data.totals.regular_hours)} />
              <Stat label="OT hours" value={fmt(data.totals.ot_hours)} accent={data.totals.ot_hours > 0 ? "amber" : null} />
              <Stat label="Total pay" value={`$${data.totals.total_pay.toLocaleString()}`} />
              <Stat label="Approved" value={`${data.totals.approved_count}/${data.employee_count}`} accent={data.totals.pending_count === 0 ? "green" : null} />
            </div>

            <TrendBar trend={data.trend} />

            {/* Rows */}
            <div className="mt-4 space-y-2">
              {rowsFiltered.map((r) => (
                <TimesheetRow
                  key={r.employee_code}
                  row={r}
                  expanded={expanded === r.employee_code}
                  onToggle={() => setExpanded(expanded === r.employee_code ? null : r.employee_code)}
                  onApprove={() => approveOne(r.employee_code)}
                  onUnapprove={() => unapproveOne(r.employee_code)}
                  onPunchChanged={load}
                />
              ))}
              {rowsFiltered.length === 0 && (
                <Card className="bg-[#1a1a1c] border-[#2a2a2c] p-10 text-center text-gray-500 text-sm">
                  No employees in this category
                </Card>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
function TimesheetRow({ row, expanded, onToggle, onApprove, onUnapprove, onPunchChanged }) {
  const isApproved = !!row.approval;
  return (
    <Card className={`bg-[#1a1a1c] border ${isApproved ? "border-green-500/30" : "border-[#2a2a2c]"}`} data-testid={`ts-row-${row.employee_code}`}>
      <div className="flex items-center gap-3 p-3 cursor-pointer hover:bg-[#1f1f21]" onClick={onToggle}>
        <button className="text-gray-500">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        <div className="flex-1 min-w-0 flex items-center gap-3 flex-wrap">
          <span className="font-mono text-xs text-gray-500 w-14">{row.employee_code}</span>
          <span className="font-semibold text-white min-w-[140px]">{row.employee_name}</span>
          <span className="text-xs text-gray-400 min-w-[80px]">{row.department}</span>
        </div>
        <div className="hidden sm:flex items-center gap-6 text-right">
          <div className="min-w-[70px]">
            <div className="text-[10px] uppercase text-gray-500 tracking-wide">Reg</div>
            <div className="font-mono text-sm">{fmt(row.regular_hours)}h</div>
          </div>
          <div className="min-w-[70px]">
            <div className="text-[10px] uppercase text-gray-500 tracking-wide">OT</div>
            <div className={`font-mono text-sm ${row.ot_hours > 0 ? "text-amber-400" : ""}`}>{fmt(row.ot_hours)}h</div>
          </div>
          <div className="min-w-[90px]">
            <div className="text-[10px] uppercase text-gray-500 tracking-wide">Pay</div>
            <div className="font-mono text-sm font-semibold">${row.total_pay.toLocaleString()}</div>
          </div>
        </div>
        {isApproved ? (
          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onUnapprove(); }} className="bg-green-500/10 border-green-500/40 text-green-300 hover:bg-green-500/20 h-8" data-testid={`ts-unapprove-${row.employee_code}`}>
            <Check className="w-3 h-3 mr-1" /> Approved
          </Button>
        ) : (
          <Button size="sm" onClick={(e) => { e.stopPropagation(); onApprove(); }} className="bg-blue-500 hover:bg-blue-600 text-white h-8" data-testid={`ts-approve-${row.employee_code}`}>
            Approve
          </Button>
        )}
      </div>

      {expanded && (
        <div className="border-t border-[#2a2a2c] p-4 bg-[#141416]" data-testid={`ts-detail-${row.employee_code}`}>
          {row.approval && (
            <div className="text-xs text-green-400 mb-3">
              ✓ Approved by {row.approval.approved_by_name} · {new Date(row.approval.approved_at).toLocaleString()}
            </div>
          )}
          <PunchesTable row={row} onChanged={onPunchChanged} />
        </div>
      )}
    </Card>
  );
}

function PunchesTable({ row, onChanged }) {
  const [editing, setEditing] = useState(null); // punch_id
  const [editVal, setEditVal] = useState("");
  const [adding, setAdding] = useState(null); // date
  const [newPunch, setNewPunch] = useState({ type: "in", timestamp: "" });

  const savePunch = async (punchId) => {
    try {
      await axios.put(`${API}/timeclock/punches/${punchId}`, { timestamp: new Date(editVal).toISOString() });
      toast.success("Updated");
      setEditing(null);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };
  const deletePunch = async (punchId) => {
    if (!window.confirm("Delete this punch?")) return;
    try {
      await axios.delete(`${API}/timeclock/punches/${punchId}`);
      toast.success("Deleted");
      onChanged();
    } catch (e) {
      toast.error("Delete failed");
    }
  };
  const addPunch = async (date) => {
    try {
      await axios.post(`${API}/timeclock/punches/add`, {
        employee_code: row.employee_code,
        punch_type: newPunch.type,
        timestamp: new Date(`${date}T${newPunch.timestamp}`).toISOString(),
      });
      toast.success("Added");
      setAdding(null);
      setNewPunch({ type: "in", timestamp: "" });
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add failed");
    }
  };

  if (row.daily.length === 0) {
    return <div className="text-sm text-gray-500 italic">No punches in this period.</div>;
  }

  return (
    <div className="space-y-2">
      {row.daily.map((d) => (
        <div key={d.date} className="flex items-start gap-3 py-2 border-b border-[#2a2a2c] last:border-b-0">
          <div className="w-24 shrink-0">
            <div className="text-xs text-gray-500 uppercase tracking-wide">{fmtWeekday(d.date)}</div>
            <div className="text-sm font-semibold">{fmtDay(d.date)}</div>
            <div className="text-[11px] text-gray-500 font-mono">{fmt(d.hours)}h</div>
          </div>
          <div className="flex-1 flex flex-wrap gap-2">
            {d.punches.map((p) => (
              <div key={p.id} className="inline-flex items-center gap-1 bg-[#252527] border border-[#3a3a3c] rounded px-2 py-1 text-xs">
                <span className={`font-semibold ${p.type.includes("out") ? "text-red-300" : "text-green-300"}`}>
                  {p.type.replace("_", " ").toUpperCase()}
                </span>
                {editing === p.id ? (
                  <>
                    <Input type="datetime-local" value={editVal} onChange={(e) => setEditVal(e.target.value)} className="bg-[#1a1a1c] border-[#3a3a3c] text-white text-xs h-6 w-40" />
                    <button onClick={() => savePunch(p.id)} className="text-green-300 hover:text-green-400" data-testid={`save-${p.id}`}><Check className="w-3 h-3" /></button>
                    <button onClick={() => setEditing(null)} className="text-gray-400"><X className="w-3 h-3" /></button>
                  </>
                ) : (
                  <>
                    <span className="text-gray-300 font-mono">{fmtTime(p.timestamp)}</span>
                    {p.edited && <span className="text-amber-400 text-[9px]">✎</span>}
                    <button onClick={() => { setEditing(p.id); setEditVal(toLocalInput(p.timestamp)); }} className="text-blue-300 hover:text-blue-400" data-testid={`edit-${p.id}`}>
                      <Edit2 className="w-3 h-3" />
                    </button>
                    <button onClick={() => deletePunch(p.id)} className="text-red-400 hover:text-red-500" data-testid={`del-${p.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </>
                )}
              </div>
            ))}
            {adding === d.date ? (
              <div className="inline-flex items-center gap-1 bg-[#252527] border border-blue-500/40 rounded px-2 py-1 text-xs">
                <select value={newPunch.type} onChange={(e) => setNewPunch({ ...newPunch, type: e.target.value })} className="bg-[#1a1a1c] border border-[#3a3a3c] text-white text-xs h-6 rounded px-1">
                  <option value="in">IN</option>
                  <option value="out">OUT</option>
                  <option value="lunch_out">LUNCH OUT</option>
                  <option value="lunch_in">LUNCH IN</option>
                </select>
                <Input type="time" value={newPunch.timestamp} onChange={(e) => setNewPunch({ ...newPunch, timestamp: e.target.value })} className="bg-[#1a1a1c] border-[#3a3a3c] text-white text-xs h-6 w-24" />
                <button onClick={() => addPunch(d.date)} className="text-green-300 hover:text-green-400" data-testid={`add-save-${d.date}`}><Check className="w-3 h-3" /></button>
                <button onClick={() => setAdding(null)} className="text-gray-400"><X className="w-3 h-3" /></button>
              </div>
            ) : (
              <button onClick={() => { setAdding(d.date); setNewPunch({ type: "in", timestamp: "09:00" }); }} className="inline-flex items-center gap-1 border border-dashed border-[#3a3a3c] rounded px-2 py-1 text-xs text-gray-500 hover:text-gray-300 hover:border-gray-500" data-testid={`add-${d.date}`}>
                <Plus className="w-3 h-3" /> Add
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
function Stat({ label, value, accent }) {
  const cls = {
    green: "text-green-400",
    amber: "text-amber-400",
    red: "text-red-400",
  }[accent] || "text-white";
  return (
    <Card className="bg-[#1a1a1c] border-[#2a2a2c] p-3">
      <div className="text-[10px] uppercase text-gray-500 tracking-widest">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${cls}`}>{value}</div>
    </Card>
  );
}

function TrendBar({ trend }) {
  if (!trend || trend.length === 0) return null;
  const max = Math.max(...trend.map((t) => t.total_hours), 1);
  return (
    <Card className="bg-[#1a1a1c] border-[#2a2a2c] p-4">
      <div className="text-xs text-gray-500 uppercase tracking-widest mb-3">Weekly labor hours trend</div>
      <div className="flex items-end gap-2 h-24">
        {trend.map((t) => (
          <div key={t.week_start} className="flex-1 flex flex-col items-center gap-1" title={`Week of ${t.week_start}: ${t.total_hours}h`}>
            <div
              className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t"
              style={{ height: `${(t.total_hours / max) * 100}%`, minHeight: "3px" }}
            />
            <div className="text-[10px] text-gray-500 font-mono">{t.week_start.slice(5)}</div>
            <div className="text-[10px] text-gray-300 font-mono">{t.total_hours}h</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════
// Helpers
function defaultBiweekly() {
  // Current Sunday-Saturday × 2 (last two full weeks incl. current)
  const now = new Date();
  const day = now.getDay(); // 0=Sun
  const end = new Date(now);
  end.setDate(now.getDate() + (6 - day)); // upcoming Saturday
  const start = new Date(end);
  start.setDate(end.getDate() - 13); // 2 weeks back Sunday
  return { start: iso(start), end: iso(end) };
}
function iso(d) { return d.toISOString().slice(0, 10); }
function addDays(dateStr, n) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + n);
  return iso(d);
}
function fmt(n) { return (Math.round(n * 100) / 100).toFixed(2); }
function fmtDate(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
function fmtDay(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
function fmtWeekday(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { weekday: "short" });
}
function fmtTime(ts) {
  try {
    return new Date(ts).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  } catch { return "—"; }
}
function toLocalInput(ts) {
  const d = new Date(ts);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
